import { createReducer, on } from '@ngrx/store';
import {
  ActivateCampaignInit,
  ActiveCampaignsChanged,
  ActiveFromEndDateChanged,
  ActiveFromStartDateChanged,
  ActiveUntilEndDateChanged,
  ActiveUntilStartDateChanged,
  AdvancedFiltersPanelChanged,
  BasicFiltersPanelChanged,
  CategoryChanged,
  ChannelChanged,
  ClearDateField,
  ClearSearchFields,
  CloseCampaignDuplicateModal,
  CloseCampaignNotesModal,
  CloseSendCampaignModal,
  DateTypeChanged,
  DeletedCampaignsChanged,
  DescriptionChanged,
  DuplicateCampaignNameChanged,
  getCategoriesSuccess,
  getCountriesSuccess,
  getSchemasSuccess,
  getStaticDataSuccess,
  MyCampaignsChanged,
  NameChanged,
  NotesChanged,
  OpenCampaignDeactivationModal,
  OpenCampaignDuplicateModal,
  OpenCampaignNotesModal,
  OpenCampaignSendModal,
  OriginalCampaignDeactivationChanged,
  SchemaChanged,
  SearchPanelChanged,
  StatusChanged,
  TargetGroupChanged,
  UseTimeChanged,
  VerificationInputChanged,
} from '../actions/campaign-search.actions';
import { ShowCampaignExecutions } from '../actions/sidenav.actions';
import { CampaignSearchState, DATE_SECTOR } from '../models/campaign-search.model';

const initialState: CampaignSearchState = {
  activeDateFromStartDate: '',
  activeDateFromEndDate: '',
  activeDateUntilEndDate: '',
  activeDateUntilStartDate: '',
  campaignCategoryId: 0,
  campaignChannel: '',
  campaignName: '',
  campaignSelectionTarget: '',
  campaignStatus: '',
  deactivated: false,
  deleted: false,
  description: '',
  loyaltySchemaId: 0,
  notes: '',
  user: false,
  masterData: {
    categories: [],
    channels: [],
    schemas: [],
    statusTypes: [],
    targetGroups: [],
    countries: [],
  },
  dateTypeFrom: 'single',
  useTimeFrom: false,
  dateTypeUntil: 'single',
  useTimeUntil: false,
  searchPanelExpanded: true,
  basicFiltersPanelExpanded: true,
  advancedFiltersPanelExpanded: false,
  selectedCampaign: null,
  verificationInput: '',
  verificationMismatch: true,
  campaignNotes: '',
  duplicateCampaignName: '',
  duplicateCampaignNameMaxLengthExceeded: false,
  originalCampaignDeactivation: true,
  campaignDeactivation: false,
};

export const CampaignSearchReducer = createReducer(
  initialState,
  on(getCategoriesSuccess, (state, { categories }) => ({
    ...state,
    masterData: {
      ...state.masterData,
      categories,
    },
  })),
  on(getSchemasSuccess, (state, { schemas }) => ({
    ...state,
    masterData: {
      ...state.masterData,
      schemas,
    },
  })),
  on(getCountriesSuccess, (state, { countries }) => ({
    ...state,
    masterData: {
      ...state.masterData,
      countries,
    },
  })),
  on(getStaticDataSuccess, (state, { staticData }) => ({
    ...state,
    masterData: {
      ...state.masterData,
      statusTypes: staticData?.statusTypes ?? [],
      targetGroups: staticData?.campaignSelectionTargetEnum ?? [],
      channels: staticData?.campaignCommunicationChannels ?? [],
    },
  })),
  on(SearchPanelChanged, (state) => ({
    ...state,
    searchPanelExpanded: !state.searchPanelExpanded,
  })),
  on(BasicFiltersPanelChanged, (state) => ({
    ...state,
    basicFiltersPanelExpanded: !state.basicFiltersPanelExpanded,
  })),
  on(AdvancedFiltersPanelChanged, (state) => ({
    ...state,
    advancedFiltersPanelExpanded: !state.advancedFiltersPanelExpanded,
  })),
  on(NameChanged, (state, { campaignName }) => ({
    ...state,
    campaignName,
  })),
  on(CategoryChanged, (state, { campaignCategoryId }) => ({
    ...state,
    campaignCategoryId,
  })),
  on(NotesChanged, (state, { notes }) => ({
    ...state,
    notes,
  })),
  on(DescriptionChanged, (state, { description }) => ({
    ...state,
    description,
  })),
  on(StatusChanged, (state, { campaignStatus }) => ({
    ...state,
    campaignStatus,
  })),
  on(TargetGroupChanged, (state, { campaignSelectionTarget }) => ({
    ...state,
    campaignSelectionTarget,
  })),
  on(SchemaChanged, (state, { loyaltySchemaId }) => ({
    ...state,
    loyaltySchemaId,
  })),
  on(ChannelChanged, (state, { campaignChannel }) => ({
    ...state,
    campaignChannel,
  })),
  on(ActiveCampaignsChanged, (state, { deactivated }) => ({
    ...state,
    deactivated,
  })),
  on(DeletedCampaignsChanged, (state, { deleted }) => ({
    ...state,
    deleted,
  })),
  on(MyCampaignsChanged, (state, { user }) => ({
    ...state,
    user,
  })),
  on(ActiveFromStartDateChanged, (state, { activeDateFromStartDate }) => ({
    ...state,
    activeDateFromStartDate,
  })),
  on(ActiveFromEndDateChanged, (state, { activeDateFromEndDate }) => ({
    ...state,
    activeDateFromEndDate,
  })),
  on(ActiveUntilStartDateChanged, (state, { activeDateUntilStartDate }) => ({
    ...state,
    activeDateUntilStartDate,
  })),
  on(ActiveUntilEndDateChanged, (state, { activeDateUntilEndDate }) => ({
    ...state,
    activeDateUntilEndDate,
  })),
  on(DateTypeChanged, (state, { dateSector, dateType }) => {
    const stateKey = dateSector === DATE_SECTOR.FROM ? 'dateTypeFrom' : 'dateTypeUntil';
    return {
      ...state,
      [stateKey]: dateType,
    };
  }),
  on(UseTimeChanged, (state, { dateSector, useTime }) => {
    const stateKey = dateSector === DATE_SECTOR.FROM ? 'useTimeFrom' : 'useTimeUntil';
    return {
      ...state,
      [stateKey]: useTime,
    };
  }),
  on(ClearDateField, (state, { dateField }) => ({
    ...state,
    [dateField]: '',
  })),
  on(ClearSearchFields, (state) => ({
    ...state,
    ...initialState,
    advancedFiltersPanelExpanded: state.advancedFiltersPanelExpanded,
    masterData: {
      ...state.masterData,
    },
  })),
  on(OpenCampaignSendModal, (state, { campaign }) => ({
    ...state,
    selectedCampaign: campaign,
  })),
  on(CloseSendCampaignModal, (state) => ({
    ...state,
    verificationInput: '',
    verificationMismatch: true,
    selectedCampaign: null,
  })),
  on(VerificationInputChanged, (state, { verificationInput }) => ({
    ...state,
    verificationInput,
    verificationMismatch: verificationInput === 'SEND' ? false : true,
  })),
  on(OpenCampaignNotesModal, (state, { notes }) => ({
    ...state,
    campaignNotes: notes,
  })),
  on(OpenCampaignDuplicateModal, (state, { campaign }) => {
    const newDuplicateName = campaign.data.campaignName.concat('_copy');
    return {
      ...state,
      selectedCampaign: campaign,
      duplicateCampaignName: newDuplicateName,
      duplicateCampaignNameMaxLengthExceeded: newDuplicateName.length > 50 ? true : false,
    };
  }),
  on(CloseCampaignDuplicateModal, (state) => ({
    ...state,
    selectedCampaign: null,
    duplicateCampaignName: '',
    duplicateCampaignNameMaxLengthExceeded: false,
    originalCampaignDeactivation: true,
  })),
  on(CloseCampaignNotesModal, (state) => ({
    ...state,
    notes: '',
  })),
  on(DuplicateCampaignNameChanged, (state, { duplicateCampaignName }) => ({
    ...state,
    duplicateCampaignName,
    duplicateCampaignNameMaxLengthExceeded: duplicateCampaignName.length > 50 ? true : false,
  })),
  on(OriginalCampaignDeactivationChanged, (state, { originalCampaignDeactivation }) => ({
    ...state,
    originalCampaignDeactivation,
  })),
  on(OpenCampaignDeactivationModal, (state, { campaign }) => ({
    ...state,
    selectedCampaign: campaign,
    campaignDeactivation: true,
  })),
  on(ActivateCampaignInit, (state, { campaign }) => ({
    ...state,
    selectedCampaign: campaign,
    campaignDeactivation: false,
  })),
  on(ShowCampaignExecutions, (state, { campaign }) => ({
    ...state,
    selectedCampaign: campaign,
  })),
);
